import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSite } from '@/contexts/SiteContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  Play,
  RefreshCw,
  AlertTriangle,
  Clock,
  FileSearch,
  Eye,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  DollarSign,
  Target,
  Shield,
  XCircle,
  HelpCircle,
  Database,
  Wrench,
  Lightbulb,
  BookOpen,
} from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { authFetch } from '@/utils/auth-fetch';
import { API_URL } from '@/config/api';
import { classifyRegenerationResponse } from '@/utils/regenerationOutcome';
import { format } from 'date-fns';

// Types
interface AuditRun {
  id: string;
  site_id: string;
  target_date: string;
  sample_size: number;
  actual_samples: number;
  status: 'pending' | 'running' | 'completed' | 'failed';
  total_generations: number;
  generations_with_content: number;
  average_quality: number;
  issues_found: number;
  critical_issues: number;
  high_issues: number;
  total_cost_usd: number;
  started_at: string;
  completed_at: string;
  error_message: string;
  created_at: string;
}

interface QualityIssue {
  id: string;
  audit_run_id: string;
  generation_id: string;
  site_id: string;
  product_name: string;
  page_url: string;
  category: string;
  issue_type: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  consensus: 'both_agree' | 'gpt_only' | 'claude_only';
  combined_confidence: number;
  description: string;
  location: string;
  quality_score_avg: number;
  status: 'open' | 'investigating' | 'fix_deployed' | 'pending_verification' | 'resolved' | 'wont_fix' | 'false_positive';
  root_cause_category: string;
  root_cause_detail: string;
  resolution_type: string;
  resolution_notes: string;
  detected_at: string;
  aeo_content?: any;
  evidence_data?: any;
}

interface IssueStats {
  total_issues: number;
  open_issues: number;
  critical_issues: number;
  high_issues: number;
  medium_issues: number;
  low_issues: number;
  both_agree: number;
  avg_quality: number;
}

const severityColors: Record<string, string> = {
  critical: 'bg-red-500',
  high: 'bg-orange-500',
  medium: 'bg-yellow-500',
  low: 'bg-blue-500',
};

const consensusLabels: Record<string, string> = {
  both_agree: 'Both Judges',
  gpt_only: 'GPT-4o Only',
  claude_only: 'Claude Only',
};

// Helper function to extract quoted text from issue description
function extractQuotedText(description: string): string[] {
  const matches = description.match(/"([^"]+)"/g) || [];
  return matches.map(m => m.replace(/"/g, ''));
}

// Helper function to highlight text with issues
function highlightText(text: string, highlights: string[]): React.ReactNode {
  if (!highlights.length || !text) return text;

  let result: React.ReactNode[] = [];
  let remainingText = text;
  let key = 0;

  for (const highlight of highlights) {
    if (!highlight || highlight.length < 3) continue;

    const lowerRemaining = remainingText.toLowerCase();
    const lowerHighlight = highlight.toLowerCase();
    const index = lowerRemaining.indexOf(lowerHighlight);

    if (index !== -1) {
      // Add text before the highlight
      if (index > 0) {
        result.push(<span key={key++}>{remainingText.substring(0, index)}</span>);
      }
      // Add highlighted text
      result.push(
        <mark key={key++} className="bg-red-200 dark:bg-red-800 text-red-900 dark:text-red-100 px-1 rounded font-medium">
          {remainingText.substring(index, index + highlight.length)}
        </mark>
      );
      remainingText = remainingText.substring(index + highlight.length);
    }
  }

  // Add remaining text
  if (remainingText) {
    result.push(<span key={key++}>{remainingText}</span>);
  }

  return result.length > 0 ? <>{result}</> : text;
}

// Component to render AEO content in a readable format
interface ContentViewerProps {
  content: any;
  issueDescription: string;
  issueLocation: string;
}

function ContentViewer({ content, issueDescription, issueLocation }: ContentViewerProps) {
  const sections = content?.sections || {};
  const highlights = extractQuotedText(issueDescription);

  // Section display names and order
  const sectionOrder = [
    { key: 'summary', label: 'Summary' },
    { key: 'key_features', label: 'Key Features' },
    { key: 'specifications', label: 'Specifications' },
    { key: 'useCases', label: 'Use Cases' },
    { key: 'use_cases', label: 'Use Cases' },
    { key: 'qaSection', label: 'FAQs' },
    { key: 'faq', label: 'FAQs' },
    { key: 'faqs', label: 'FAQs' },
    { key: 'benefitsExplanation', label: 'Benefits' },
    { key: 'benefits', label: 'Benefits' },
    { key: 'bestFor', label: 'Best For' },
    { key: 'best_for', label: 'Best For' },
    { key: 'comparisonAnalysis', label: 'Comparison' },
    { key: 'comparison', label: 'Comparison' },
    { key: 'limitations', label: 'Limitations' },
    { key: 'buyerProfiles', label: 'Buyer Profiles' },
    { key: 'buyer_profiles', label: 'Buyer Profiles' },
    { key: 'problemSolver', label: 'Problems Solved' },
    { key: 'recommendations', label: 'Recommendations' },
    { key: 'comparisonProducts', label: 'Comparison Products', isComplex: true },
    { key: 'comparison_products', label: 'Comparison Products', isComplex: true },
  ];

  // Special renderer for comparison products section (very complex nested structure)
  const renderComparisonProducts = (data: any): React.ReactNode => {
    if (!data) return null;

    return (
      <div className="space-y-4">
        {/* Current Product */}
        {data.current_product && (
          <div className="p-2 bg-primary/10 rounded border border-primary/30">
            <p className="text-xs text-muted-foreground">Current Product</p>
            <p className="font-medium">{data.current_product}</p>
          </div>
        )}

        {/* Competitors */}
        {data.competitor_alternatives?.products && data.competitor_alternatives.products.length > 0 && (
          <div>
            <p className="font-medium mb-2">Competitor Alternatives</p>
            <div className="grid grid-cols-2 gap-2">
              {data.competitor_alternatives.products.map((prod: any, i: number) => (
                <div key={i} className="p-2 bg-background rounded border border-border/50 text-sm">
                  <p className="font-medium truncate">{typeof prod === 'string' ? prod : prod.product_name || prod.name || 'Product'}</p>
                  {prod.price && <p className="text-muted-foreground">{prod.price}</p>}
                  {prod.similarity && <p className="text-xs text-muted-foreground">Similarity: {(prod.similarity * 100).toFixed(0)}%</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Attribute Comparison Summary */}
        {data.attribute_comparison?.rows && (
          <div>
            <p className="font-medium mb-2">Attribute Comparison ({data.attribute_comparison.total_attributes || data.attribute_comparison.rows.length} attributes)</p>
            <div className="space-y-1">
              {data.attribute_comparison.rows.slice(0, 5).map((row: any, i: number) => {
                const currentValue = row.values?.find((v: any) => v.is_current);
                return (
                  <div key={i} className="flex justify-between items-center p-2 bg-background rounded text-sm">
                    <span className="font-medium">{row.display_name || row.attribute}</span>
                    <span className="text-muted-foreground">{currentValue?.value || '-'}</span>
                  </div>
                );
              })}
              {data.attribute_comparison.rows.length > 5 && (
                <p className="text-xs text-muted-foreground">...and {data.attribute_comparison.rows.length - 5} more attributes</p>
              )}
            </div>
          </div>
        )}

        {/* Tradeoff Summary */}
        {data.tradeoff_summary && (
          <div className="p-2 bg-background rounded border border-border/50">
            <p className="font-medium">Tradeoff: {data.tradeoff_summary.verdict || 'Balanced'}</p>
            <p className="text-sm">Wins: {data.tradeoff_summary.wins || 0} | Losses: {data.tradeoff_summary.losses || 0}</p>
          </div>
        )}

        {/* Stats Summary */}
        {data.competitor_alternatives?.stats && (
          <div className="text-xs text-muted-foreground">
            {data.competitor_alternatives.stats.products_in_price_range} products in price range |
            Avg similarity: {((data.competitor_alternatives.stats.avg_similarity || 0) * 100).toFixed(0)}%
          </div>
        )}
      </div>
    );
  };

  // Render a structured object item (use case, benefit, best_for, etc.)
  const renderStructuredItem = (item: any, index: number): React.ReactNode => {
    // FAQ format
    if (item.question && item.answer) {
      return (
        <div key={index} className="p-2 bg-background rounded border border-border/50 mb-2">
          <p className="font-medium">{highlightText(item.question, highlights)}</p>
          <p className="text-muted-foreground mt-1">{highlightText(item.answer, highlights)}</p>
        </div>
      );
    }

    // Use case format
    if (item.use_case) {
      return (
        <div key={index} className="p-2 bg-background rounded border border-border/50 mb-2">
          <p className="font-medium">{highlightText(item.use_case, highlights)}</p>
          {item.fit_rating && <span className="text-xs text-muted-foreground ml-2">({item.fit_rating})</span>}
          {item.why_suitable && <p className="text-sm mt-1">{highlightText(item.why_suitable, highlights)}</p>}
          {item.key_features && (
            <p className="text-xs text-muted-foreground mt-1">Features: {item.key_features.join(', ')}</p>
          )}
        </div>
      );
    }

    // Benefit format
    if (item.benefit_name || item.headline) {
      return (
        <div key={index} className="p-2 bg-background rounded border border-border/50 mb-2">
          <p className="font-medium">{highlightText(item.headline || item.benefit_name, highlights)}</p>
          {item.explanation && <p className="text-sm mt-1">{highlightText(item.explanation, highlights)}</p>}
          {item.evidence && (
            <p className="text-xs text-muted-foreground mt-1">
              Evidence: {Array.isArray(item.evidence) ? item.evidence.join(', ') : item.evidence}
            </p>
          )}
        </div>
      );
    }

    // Best for / badge format
    if (item.badge_text || item.buyer_type) {
      return (
        <div key={index} className="p-2 bg-background rounded border border-border/50 mb-2">
          <p className="font-medium">{highlightText(item.badge_text || `Best for ${item.buyer_type}`, highlights)}</p>
          {item.reason && <p className="text-sm mt-1">{highlightText(item.reason, highlights)}</p>}
          {item.evidence && <p className="text-xs text-muted-foreground mt-1">Evidence: {item.evidence}</p>}
        </div>
      );
    }

    // Limitation format
    if (item.limitation) {
      return (
        <div key={index} className="p-2 bg-background rounded border border-border/50 mb-2">
          <p className="font-medium">{highlightText(item.limitation, highlights)}</p>
          {item.impact && <p className="text-sm mt-1">{highlightText(item.impact, highlights)}</p>}
          {item.mitigation && (
            <p className="text-xs text-muted-foreground mt-1">Mitigation: {item.mitigation}</p>
          )}
        </div>
      );
    }

    // Problem solver format
    if (item.problem) {
      return (
        <div key={index} className="p-2 bg-background rounded border border-border/50 mb-2">
          <p className="font-medium">{highlightText(item.problem, highlights)}</p>
          {item.how_it_solves && <p className="text-sm mt-1">{highlightText(item.how_it_solves, highlights)}</p>}
        </div>
      );
    }

    // Buyer profile format
    if (item.profile_name) {
      return (
        <div key={index} className="p-2 bg-background rounded border border-border/50 mb-2">
          <p className="font-medium">{highlightText(item.profile_name, highlights)}</p>
          {item.description && <p className="text-sm mt-1">{highlightText(item.description, highlights)}</p>}
          {item.why_this_product && (
            <p className="text-xs text-muted-foreground mt-1">{item.why_this_product}</p>
          )}
        </div>
      );
    }

    // Comparison product format
    if (item.product_name || item.brand || item.competitor_name) {
      return (
        <div key={index} className="p-2 bg-background rounded border border-border/50 mb-2">
          <p className="font-medium">{highlightText(item.product_name || item.competitor_name || item.brand, highlights)}</p>
          {item.price && <span className="text-sm text-muted-foreground mr-2">{item.price}</span>}
          {item.comparison_point && <p className="text-sm mt-1">{highlightText(item.comparison_point, highlights)}</p>}
          {item.advantage && <p className="text-sm text-green-600 dark:text-green-400 mt-1">✓ {item.advantage}</p>}
          {item.disadvantage && <p className="text-sm text-red-600 dark:text-red-400 mt-1">✗ {item.disadvantage}</p>}
        </div>
      );
    }

    // Recommendation format
    if (item.recommendation || item.suggestion) {
      return (
        <div key={index} className="p-2 bg-background rounded border border-border/50 mb-2">
          <p className="font-medium">{highlightText(item.recommendation || item.suggestion, highlights)}</p>
          {item.reason && <p className="text-sm mt-1 text-muted-foreground">{highlightText(item.reason, highlights)}</p>}
          {item.priority && <Badge variant="outline" className="mt-1">{item.priority}</Badge>}
        </div>
      );
    }

    // "Not ideal for" format
    if (item.user_type || item.audience) {
      return (
        <div key={index} className="p-2 bg-background rounded border border-border/50 mb-2">
          <p className="font-medium">{highlightText(item.user_type || item.audience, highlights)}</p>
          {item.reason && <p className="text-sm mt-1 text-muted-foreground">{highlightText(item.reason, highlights)}</p>}
        </div>
      );
    }

    // Attribute comparison row format (from comparison products)
    if (item.attribute && item.values && Array.isArray(item.values)) {
      const currentValue = item.values.find((v: any) => v.is_current);
      return (
        <div key={index} className="p-2 bg-background rounded border border-border/50 mb-2">
          <p className="font-medium">{item.display_name || item.attribute.replace(/_/g, ' ')}</p>
          <p className="text-sm">
            {currentValue ? String(currentValue.value) : '-'}
            {item.winner && <span className="text-green-600 dark:text-green-400 ml-2 text-xs">✓ Winner</span>}
          </p>
          {item.comparison_note && <p className="text-xs text-muted-foreground">{item.comparison_note}</p>}
        </div>
      );
    }

    // Competitor alternative format
    if (item.similarity !== undefined || item.avg_similarity !== undefined) {
      // This is a stats object, render summary
      return (
        <div key={index} className="p-2 bg-background rounded border border-border/50 mb-2 text-sm">
          {item.avg_similarity && <p>Avg Similarity: {(item.avg_similarity * 100).toFixed(1)}%</p>}
          {item.total_in_category && <p>Products in Category: {item.total_in_category}</p>}
          {item.products_in_price_range && <p>In Price Range: {item.products_in_price_range}</p>}
        </div>
      );
    }

    // Tradeoff summary format
    if (item.wins !== undefined || item.losses !== undefined) {
      return (
        <div key={index} className="p-2 bg-background rounded border border-border/50 mb-2 text-sm">
          <p className="font-medium">{item.verdict || 'Tradeoff Summary'}</p>
          <p>Wins: {item.wins || 0} | Losses: {item.losses || 0}</p>
          {item.advantages && item.advantages.length > 0 && (
            <p className="text-green-600 dark:text-green-400">✓ {item.advantages.join(', ')}</p>
          )}
          {item.disadvantages && item.disadvantages.length > 0 && (
            <p className="text-red-600 dark:text-red-400">✗ {item.disadvantages.join(', ')}</p>
          )}
        </div>
      );
    }

    // Fallback: render as key-value pairs
    return (
      <div key={index} className="p-2 bg-background rounded border border-border/50 mb-2 text-sm">
        {Object.entries(item).map(([k, v]) => (
          <div key={k}>
            <span className="font-medium">{k.replace(/_/g, ' ')}:</span>{' '}
            <span>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderValue = (value: any): React.ReactNode => {
    if (!value) return null;

    if (typeof value === 'string') {
      return <p className="text-sm [word-break:break-word]">{highlightText(value, highlights)}</p>;
    }

    if (Array.isArray(value)) {
      // Check if it's an array of structured objects
      if (value.length > 0 && typeof value[0] === 'object') {
        return <div className="space-y-1">{value.map((item, i) => renderStructuredItem(item, i))}</div>;
      }
      // Simple array of strings
      return (
        <ul className="list-disc list-inside space-y-1">
          {value.map((item, i) => (
            <li key={i} className="text-sm [word-break:break-word]">
              {highlightText(String(item), highlights)}
            </li>
          ))}
        </ul>
      );
    }

    if (typeof value === 'object') {
      return (
        <div className="space-y-3 text-sm">
          {Object.entries(value).map(([k, v]) => {
            // Handle nested arrays of objects (like limitations.limitations, buyerProfiles.profiles)
            if (Array.isArray(v) && v.length > 0 && typeof v[0] === 'object') {
              return (
                <div key={k}>
                  <p className="font-medium mb-2 capitalize">{k.replace(/_/g, ' ')}:</p>
                  <div className="pl-2 border-l-2 border-border">
                    {v.map((item, i) => renderStructuredItem(item, i))}
                  </div>
                </div>
              );
            }
            // Handle nested arrays of primitives
            if (Array.isArray(v)) {
              return (
                <div key={k}>
                  <span className="font-medium capitalize">{k.replace(/_/g, ' ')}:</span>{' '}
                  <span className="[word-break:break-word]">{v.join(', ')}</span>
                </div>
              );
            }
            // Handle nested objects (recurse)
            if (v && typeof v === 'object') {
              return (
                <div key={k}>
                  <p className="font-medium mb-1 capitalize">{k.replace(/_/g, ' ')}:</p>
                  <div className="pl-2 border-l-2 border-border">
                    {renderValue(v)}
                  </div>
                </div>
              );
            }
            // Handle primitives
            return (
              <div key={k}>
                <span className="font-medium capitalize">{k.replace(/_/g, ' ')}:</span>{' '}
                <span className="[word-break:break-word]">{highlightText(String(v), highlights)}</span>
              </div>
            );
          })}
        </div>
      );
    }

    return <span className="text-sm [word-break:break-word]">{highlightText(String(value), highlights)}</span>;
  };

  return (
    <div className="space-y-4 [word-break:break-word]">
      {/* Product info header */}
      {content?.product_name && (
        <div className="border-b pb-2">
          <h4 className="font-medium">{content.product_name}</h4>
          {content?.metadata?.category && (
            <p className="text-sm text-muted-foreground">Category: {content.metadata.category}</p>
          )}
        </div>
      )}

      {/* Issue location indicator */}
      {issueLocation && (
        <Alert className="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950">
          <AlertTriangle className="h-4 w-4 text-red-500" />
          <AlertDescription className="text-red-700 dark:text-red-300">
            <strong>Issue Location:</strong> {issueLocation}
          </AlertDescription>
        </Alert>
      )}

      {/* Sections */}
      {sectionOrder.map(({ key, label, isComplex }) => {
        const sectionContent = sections[key];
        if (!sectionContent) return null;

        // Check if this section is mentioned in the issue
        const isRelevantSection = issueLocation?.toLowerCase().includes(key.toLowerCase());

        return (
          <div
            key={key}
            className={`p-3 rounded border overflow-hidden ${
              isRelevantSection
                ? 'border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950'
                : 'border-border bg-muted'
            }`}
          >
            <h5 className={`font-medium mb-2 ${isRelevantSection ? 'text-red-700 dark:text-red-300' : ''}`}>
              {label}
              {isRelevantSection && (
                <Badge variant="destructive" className="ml-2 text-xs">
                  Issue Here
                </Badge>
              )}
            </h5>
            {isComplex && (key === 'comparisonProducts' || key === 'comparison_products')
              ? renderComparisonProducts(sectionContent)
              : renderValue(sectionContent)}
          </div>
        );
      })}

      {/* Show any other sections not in the predefined list */}
      {Object.entries(sections)
        .filter(([key]) => !sectionOrder.find(s => s.key === key))
        .map(([key, value]) => (
          <div key={key} className="p-3 rounded border border-border bg-muted overflow-hidden">
            <h5 className="font-medium mb-2 capitalize">{key.replace(/_/g, ' ')}</h5>
            {renderValue(value)}
          </div>
        ))}

      {/* Raw JSON toggle for developers */}
      <details className="text-xs">
        <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
          Show Raw JSON
        </summary>
        <pre className="mt-2 bg-muted p-3 rounded overflow-auto max-h-40">
          {JSON.stringify(content, null, 2)}
        </pre>
      </details>
    </div>
  );
}

export function ProductQualityAudit() {
  const { selectedSite } = useSite();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('overview');

  // Audit runs state
  const [auditRuns, setAuditRuns] = useState<AuditRun[]>([]);
  const [loadingRuns, setLoadingRuns] = useState(false);

  // Issues state
  const [issues, setIssues] = useState<QualityIssue[]>([]);
  const [loadingIssues, setLoadingIssues] = useState(false);
  const [issueFilter, setIssueFilter] = useState<{ status?: string; severity?: string }>({});

  // Stats state
  const [stats, setStats] = useState<IssueStats | null>(null);

  // New audit dialog
  const [showNewAudit, setShowNewAudit] = useState(false);
  const [newAuditDate, setNewAuditDate] = useState(new Date().toISOString().split('T')[0]);
  const [newAuditSampleSize, setNewAuditSampleSize] = useState(50);
  const [startingAudit, setStartingAudit] = useState(false);

  // Issue detail dialog
  const [selectedIssue, setSelectedIssue] = useState<QualityIssue | null>(null);
  const [loadingEvidence, setLoadingEvidence] = useState(false);

  // Error state
  const [error, setError] = useState<string | null>(null);

  // Regeneration, verification, and research state
  const [regenerating, setRegenerating] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [applyingResearch, setApplyingResearch] = useState(false);
  const [researchResult, setResearchResult] = useState<{ success: boolean; message: string } | null>(null);
  const [regenerationJob, setRegenerationJob] = useState<{
    jobId: string;
    status: string;
    message: string;
  } | null>(null);

  // Fetch audit runs
  const fetchAuditRuns = useCallback(async () => {
    if (!selectedSite) return;

    setLoadingRuns(true);
    try {
      const response = await authFetch(
        `${API_URL}/quality-audit/runs?siteId=${selectedSite.id || selectedSite._id}`
      );
      const data = await response.json();
      setAuditRuns(data.runs || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch audit runs');
    } finally {
      setLoadingRuns(false);
    }
  }, [selectedSite]);

  // Fetch issues
  const fetchIssues = useCallback(async () => {
    if (!selectedSite) return;

    setLoadingIssues(true);
    try {
      let url = `${API_URL}/quality-audit/issues?siteId=${selectedSite.id || selectedSite._id}`;
      if (issueFilter.status) url += `&status=${issueFilter.status}`;
      if (issueFilter.severity) url += `&severity=${issueFilter.severity}`;

      const response = await authFetch(url);
      const data = await response.json();
      setIssues(data.issues || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch issues');
    } finally {
      setLoadingIssues(false);
    }
  }, [selectedSite, issueFilter]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    if (!selectedSite) return;

    try {
      const response = await authFetch(
        `${API_URL}/quality-audit/stats?siteId=${selectedSite.id || selectedSite._id}`
      );
      const data = await response.json();
      setStats(data.stats);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, [selectedSite]);

  // Start new audit
  const startAudit = async () => {
    if (!selectedSite) return;

    setStartingAudit(true);
    try {
      const response = await authFetch(`${API_URL}/quality-audit/runs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteId: selectedSite.id || selectedSite._id,
          targetDate: newAuditDate,
          sampleSize: newAuditSampleSize,
        }),
      });
      const data = await response.json();

      if (data.success) {
        setShowNewAudit(false);
        fetchAuditRuns();
      } else {
        setError(data.error || 'Failed to start audit');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start audit');
    } finally {
      setStartingAudit(false);
    }
  };

  // Fetch issue detail with evidence
  const fetchIssueDetail = async (issueId: string) => {
    try {
      const response = await authFetch(`${API_URL}/quality-audit/issues/${issueId}`);
      const data = await response.json();
      setSelectedIssue(data.issue);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch issue details');
    }
  };

  // Retrieve evidence
  const retrieveEvidence = async (issueId: string) => {
    setLoadingEvidence(true);
    try {
      const response = await authFetch(`${API_URL}/quality-audit/issues/${issueId}/evidence`, {
        method: 'POST',
      });
      const data = await response.json();

      if (data.success) {
        // Refresh issue detail
        fetchIssueDetail(issueId);
      } else {
        setError(data.message || 'Failed to retrieve evidence');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to retrieve evidence');
    } finally {
      setLoadingEvidence(false);
    }
  };

  // Update issue status
  const updateIssueStatus = async (issueId: string, updates: Partial<QualityIssue>) => {
    try {
      const response = await authFetch(`${API_URL}/quality-audit/issues/${issueId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      const data = await response.json();

      if (data.success) {
        fetchIssues();
        if (selectedIssue?.id === issueId) {
          setSelectedIssue(data.issue);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update issue');
    }
  };

  // Poll for job status
  const pollJobStatus = useCallback(async (jobId: string, issueId: string) => {
    const maxAttempts = 60; // 2 minutes max
    let attempts = 0;

    const poll = async () => {
      try {
        const response = await authFetch(`${API_URL}/jobs/${jobId}`);
        const data = await response.json();

        if (data.job) {
          const job = data.job;
          if (job.status === 'completed') {
            setRegenerationJob({
              jobId,
              status: 'completed',
              message: 'Regeneration complete! Click "Verify Fix" to check if the issue is resolved.',
            });
            setRegenerating(false);
            fetchIssueDetail(issueId);
            fetchIssues();
            return;
          } else if (job.status === 'failed') {
            setRegenerationJob({
              jobId,
              status: 'failed',
              message: `Regeneration failed: ${job.error || 'Unknown error'}`,
            });
            setRegenerating(false);
            setError(`Regeneration failed: ${job.error || 'Unknown error'}`);
            return;
          } else {
            // Still processing
            setRegenerationJob({
              jobId,
              status: job.status,
              message: `Regenerating content... (${job.status})`,
            });
          }
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000); // Poll every 2 seconds
        } else {
          setRegenerationJob({
            jobId,
            status: 'timeout',
            message: 'Regeneration is taking longer than expected. Check back later.',
          });
          setRegenerating(false);
        }
      } catch (err) {
        console.error('Error polling job status:', err);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        }
      }
    };

    poll();
  }, [fetchIssueDetail, fetchIssues]);

  // Trigger regeneration for an issue
  const regenerateIssue = async (issueId: string) => {
    setRegenerating(true);
    setRegenerationJob(null);
    try {
      const response = await authFetch(`${API_URL}/quality-audit/issues/${issueId}/regenerate`, {
        method: 'POST',
      });
      const data = await response.json().catch(() => null);

      // ⛔ `response.ok` was never checked here either. This route's error paths return
      // `{ error }` with NO `success` field, so the final else happened to catch them — but that
      // is a property of the route's current error shape, not a check. A 502 returning HTML, or an
      // error body that ever gains `success: true`, would walk straight past it.
      const outcome = classifyRegenerationResponse(response.ok, response.status, data);
      if (outcome.kind === 'failed') {
        setError(outcome.message);
        setRegenerating(false);
        return;
      }

      if (outcome.kind === 'queued') {
        setRegenerationJob({
          jobId: outcome.jobId,
          status: 'pending',
          message: 'Regeneration job created. Processing...',
        });
        // Start polling for job completion
        pollJobStatus(outcome.jobId, issueId);
      } else if (outcome.kind === 'completed') {
        // A genuine synchronous completion: the write is done, so refreshing shows real state.
        // This route does not currently produce it — every success carries a jobId — but handling
        // it explicitly means a future sync response is not funnelled into the error below.
        fetchIssueDetail(issueId);
        fetchIssues();
        setRegenerating(false);
      } else {
        // ⛔⛔ THE LATENT BRANCH, CLOSED. This used to read "No job tracking, fall back to
        // immediate refresh" — it refreshed the lists and cleared the spinner, i.e. reported
        // COMPLETION, for a response that acknowledged the request without evidencing any work.
        // It was correct only because this endpoint happens to return a jobId today: A HAZARD
        // UNREACHABLE ONLY BECAUSE OF CURRENT BEHAVIOUR IS SCHEDULED, NOT ABSENT. A default
        // change, a degraded path or a sync fallback would have made this page silently claim a
        // regeneration that never ran.
        // ⇒ Accepted-without-a-job is now surfaced as exactly that, and the lists are NOT
        // refreshed as though something had been written.
        setError(
          'The server accepted the regeneration but did not return a job to track, so its progress '
          + 'cannot be confirmed here. Refresh the page shortly to see whether it ran.'
        );
        setRegenerating(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to trigger regeneration');
      setRegenerating(false);
    }
  };

  // Verify issue after regeneration
  const verifyIssue = async (issueId: string) => {
    setVerifying(true);
    try {
      const response = await authFetch(`${API_URL}/quality-audit/issues/${issueId}/verify`, {
        method: 'POST',
      });
      const data = await response.json();

      if (data.success) {
        // Refresh issue detail
        fetchIssueDetail(issueId);
        fetchIssues();
      } else {
        setError(data.error || 'Failed to verify issue');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to verify issue');
    } finally {
      setVerifying(false);
    }
  };

  // Apply research suggestion from agent diagnosis
  const applyResearch = async (issueId: string) => {
    setApplyingResearch(true);
    setResearchResult(null);
    try {
      const response = await authFetch(`${API_URL}/quality-audit/issues/${issueId}/apply-research`, {
        method: 'POST',
      });
      const data = await response.json();

      if (data.success) {
        setResearchResult({
          success: true,
          message: `Research applied: ${data.operation} "${data.concept_name}" to ${data.category_path}. ${data.next_step}`,
        });
        // Refresh issue detail to show updated status
        fetchIssueDetail(issueId);
      } else {
        setResearchResult({
          success: false,
          message: data.error || 'Failed to apply research',
        });
      }
    } catch (err) {
      setResearchResult({
        success: false,
        message: err instanceof Error ? err.message : 'Failed to apply research',
      });
    } finally {
      setApplyingResearch(false);
    }
  };

  // Effects
  useEffect(() => {
    fetchAuditRuns();
    fetchStats();
  }, [fetchAuditRuns, fetchStats]);

  useEffect(() => {
    if (activeTab === 'issues') {
      fetchIssues();
    }
  }, [activeTab, fetchIssues]);

  // Refresh running audits
  useEffect(() => {
    const hasRunning = auditRuns.some(r => r.status === 'running' || r.status === 'pending');
    if (hasRunning) {
      const interval = setInterval(fetchAuditRuns, 5000);
      return () => clearInterval(interval);
    }
  }, [auditRuns, fetchAuditRuns]);

  // Handle issueId from URL parameter (e.g., from action inbox)
  useEffect(() => {
    const issueId = searchParams.get('issueId');
    if (issueId && selectedSite) {
      // Switch to issues tab
      setActiveTab('issues');
      // Fetch and open the specific issue
      const fetchAndOpenIssue = async () => {
        try {
          const response = await authFetch(`${API_URL}/quality-audit/issues/${issueId}`);
          if (response.ok) {
            const data = await response.json();
            if (data.issue) {
              setSelectedIssue(data.issue);
              // Clear the URL parameter after opening
              setSearchParams({}, { replace: true });
            }
          }
        } catch (err) {
          console.error('Failed to fetch issue from URL param:', err);
        }
      };
      fetchAndOpenIssue();
    }
  }, [searchParams, selectedSite, setSearchParams]);

  if (!selectedSite) {
    return (
      <div className="p-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Please select a site to view quality audit.</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Deprecation Banner */}
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 flex items-center justify-between">
        <p className="text-sm text-yellow-800 dark:text-yellow-200">
          This page is being consolidated into the unified <strong>Data Quality</strong> dashboard.
        </p>
        <a href="/data-quality" className="text-sm font-medium text-yellow-700 dark:text-yellow-300 hover:underline whitespace-nowrap ml-4">
          Go to Data Quality &rarr;
        </a>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Quality Audit</h1>
          <p className="text-muted-foreground">
            LLM-as-Judge quality assurance for AI-generated content
          </p>
        </div>
        <Dialog open={showNewAudit} onOpenChange={setShowNewAudit}>
          <DialogTrigger asChild>
            <Button>
              <Play className="h-4 w-4 mr-2" />
              Start New Audit
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Start Quality Audit</DialogTitle>
              <DialogDescription>
                Run a dual-judge evaluation on recent content generations.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Target Date</Label>
                <Input
                  type="date"
                  value={newAuditDate}
                  onChange={(e) => setNewAuditDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Sample Size</Label>
                <Input
                  type="number"
                  min={1}
                  max={200}
                  value={newAuditSampleSize}
                  onChange={(e) => setNewAuditSampleSize(parseInt(e.target.value) || 50)}
                />
                <p className="text-sm text-muted-foreground">
                  Number of generations to evaluate (max 200)
                </p>
              </div>
              <Alert>
                <DollarSign className="h-4 w-4" />
                <AlertDescription>
                  Estimated cost: ~${(newAuditSampleSize * 0.033).toFixed(2)} (dual-judge evaluation)
                </AlertDescription>
              </Alert>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowNewAudit(false)}>
                Cancel
              </Button>
              <Button onClick={startAudit} disabled={startingAudit}>
                {startingAudit ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Starting...
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    Start Audit
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Content Manager Guidance */}
      <Collapsible>
        <Card className="border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/30">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <HelpCircle className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                <CardTitle className="text-base text-blue-900 dark:text-blue-100">How to Use Quality Audit</CardTitle>
              </div>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm">
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </CollapsibleTrigger>
            </div>
          </CardHeader>
          <CollapsibleContent>
            <CardContent className="pt-0 space-y-4 text-sm">
              {/* Quick Actions */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-3 bg-white dark:bg-background rounded-lg border">
                  <div className="flex items-center gap-2 mb-2">
                    <Database className="h-4 w-4 text-orange-500" />
                    <span className="font-medium">Feed Data Issues</span>
                  </div>
                  <p className="text-muted-foreground text-xs mb-2">
                    Filter by <Badge variant="outline" className="text-xs">Won't Fix</Badge> to see issues caused by incorrect product feed data.
                  </p>
                  <p className="text-xs"><strong>Action:</strong> Fix the data in your product feed. The next import will replace the wrong values.</p>
                </div>

                <div className="p-3 bg-white dark:bg-background rounded-lg border">
                  <div className="flex items-center gap-2 mb-2">
                    <Wrench className="h-4 w-4 text-blue-500" />
                    <span className="font-medium">After Feed Fix</span>
                  </div>
                  <p className="text-muted-foreground text-xs mb-2">
                    Once you've corrected the feed data and it's been re-imported:
                  </p>
                  <p className="text-xs"><strong>Action:</strong> Open the issue → Click "Regenerate Content" → Then "Verify Fix"</p>
                </div>

                <div className="p-3 bg-white dark:bg-background rounded-lg border">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    <span className="font-medium">Acceptable Limitation</span>
                  </div>
                  <p className="text-muted-foreground text-xs mb-2">
                    If the issue is minor or the current content is acceptable:
                  </p>
                  <p className="text-xs"><strong>Action:</strong> Change status to "Resolved" and add a note explaining why it's acceptable.</p>
                </div>
              </div>

              {/* Status Guide */}
              <div className="border-t pt-4">
                <p className="font-medium mb-2">Issue Statuses Explained</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  <div className="flex items-center gap-1">
                    <Badge variant="outline">Open</Badge>
                    <span className="text-muted-foreground">Needs review</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Badge variant="outline" className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">Pending Verification</Badge>
                    <span className="text-muted-foreground">Regenerated, verify fix</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Badge variant="outline" className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">Fix Deployed</Badge>
                    <span className="text-muted-foreground">Code fixed, needs regen</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Badge variant="default">Resolved</Badge>
                    <span className="text-muted-foreground">Issue fixed</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Badge variant="secondary">Won't Fix</Badge>
                    <span className="text-muted-foreground">Feed data issue</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Badge variant="secondary">False Positive</Badge>
                    <span className="text-muted-foreground">AI judge was wrong</span>
                  </div>
                </div>
              </div>

              {/* Workflow Tips */}
              <div className="border-t pt-4">
                <p className="font-medium mb-2">Recommended Workflow</p>
                <ol className="list-decimal list-inside space-y-1 text-xs text-muted-foreground">
                  <li><strong>Won't Fix issues:</strong> These are feed data problems. Fix in your product data source, then regenerate.</li>
                  <li><strong>Fix Deployed issues:</strong> A code fix was deployed. Click "Regenerate Content" to apply it.</li>
                  <li><strong>Pending Verification:</strong> Content was regenerated. Click "Verify Fix" to confirm the issue is resolved.</li>
                  <li><strong>Open issues:</strong> Review the content and decide: regenerate, mark as won't fix, or mark as false positive.</li>
                </ol>
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Total Issues</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_issues}</div>
              <p className="text-xs text-muted-foreground">
                {stats.open_issues} open
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-red-600">Critical</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{stats.critical_issues}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-orange-600">High</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">{stats.high_issues}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Avg Quality</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats.avg_quality ? `${Number(stats.avg_quality).toFixed(1)}/10` : 'N/A'}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Audit Runs</TabsTrigger>
          <TabsTrigger value="issues">Issues ({stats?.total_issues ?? issues.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          {loadingRuns ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : auditRuns.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <FileSearch className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No audit runs yet.</p>
                <Button className="mt-4" onClick={() => setShowNewAudit(true)}>
                  Start Your First Audit
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {auditRuns.map((run) => (
                <Card key={run.id}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-lg">
                          {format(new Date(run.target_date), 'MMM d, yyyy')}
                        </CardTitle>
                        <Badge
                          variant={
                            run.status === 'completed'
                              ? 'default'
                              : run.status === 'running'
                              ? 'secondary'
                              : run.status === 'failed'
                              ? 'destructive'
                              : 'outline'
                          }
                        >
                          {run.status === 'running' && (
                            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          )}
                          {run.status}
                        </Badge>
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {format(new Date(run.created_at), 'MMM d, HH:mm')}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {run.status === 'running' ? (
                      <div className="space-y-2">
                        <Progress value={30} className="h-2" />
                        <p className="text-sm text-muted-foreground">
                          Evaluating samples...
                        </p>
                      </div>
                    ) : run.status === 'completed' ? (
                      <div className="grid grid-cols-5 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Samples</p>
                          <p className="font-medium">
                            {run.actual_samples} / {run.total_generations}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Quality</p>
                          <p className="font-medium">
                            {run.average_quality != null ? Number(run.average_quality).toFixed(1) : 'N/A'}/10
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Issues</p>
                          <p className="font-medium">
                            {run.issues_found}
                            {run.critical_issues > 0 && (
                              <span className="text-red-500 ml-1">
                                ({run.critical_issues} critical)
                              </span>
                            )}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Cost</p>
                          <p className="font-medium">${run.total_cost_usd != null ? Number(run.total_cost_usd).toFixed(4) : '0.0000'}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Duration</p>
                          <p className="font-medium">
                            {run.completed_at && run.started_at
                              ? `${Math.round(
                                  (new Date(run.completed_at).getTime() -
                                    new Date(run.started_at).getTime()) /
                                    1000
                                )}s`
                              : '-'}
                          </p>
                        </div>
                      </div>
                    ) : run.status === 'failed' ? (
                      <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{run.error_message}</AlertDescription>
                      </Alert>
                    ) : (
                      <p className="text-sm text-muted-foreground">Waiting to start...</p>
                    )}

                    {run.generations_with_content !== undefined &&
                      run.total_generations > 0 &&
                      run.generations_with_content < run.total_generations && (
                        <Alert className="mt-4">
                          <AlertTriangle className="h-4 w-4" />
                          <AlertDescription>
                            {run.total_generations - run.generations_with_content} of{' '}
                            {run.total_generations} generations have no content
                          </AlertDescription>
                        </Alert>
                      )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="issues" className="space-y-4">
          {/* Filters */}
          <div className="flex gap-4">
            <Select
              value={issueFilter.status || 'all'}
              onValueChange={(v) =>
                setIssueFilter((f) => ({ ...f, status: v === 'all' ? undefined : v }))
              }
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="investigating">Investigating</SelectItem>
                <SelectItem value="fix_deployed">Fix Deployed</SelectItem>
                <SelectItem value="pending_verification">Pending Verification</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="wont_fix">Won't Fix</SelectItem>
                <SelectItem value="false_positive">False Positive</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={issueFilter.severity || 'all'}
              onValueChange={(v) =>
                setIssueFilter((f) => ({ ...f, severity: v === 'all' ? undefined : v }))
              }
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Severity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Severity</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchIssues}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>

          {/* Issues List */}
          {loadingIssues ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : issues.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <CheckCircle2 className="h-12 w-12 mx-auto text-green-500 mb-4" />
                <p className="text-muted-foreground">No issues found.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {issues.map((issue) => (
                <Card
                  key={issue.id}
                  className="cursor-pointer hover:bg-accent/50 transition-colors"
                  onClick={() => {
                    fetchIssueDetail(issue.id);
                  }}
                >
                  <CardContent className="py-4">
                    <div className="flex items-start gap-4">
                      <div
                        className={`w-2 h-2 mt-2 rounded-full ${
                          severityColors[issue.severity]
                        }`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline">{issue.issue_type}</Badge>
                          <Badge variant="secondary">
                            {consensusLabels[issue.consensus]}
                          </Badge>
                          {issue.status !== 'open' && (
                            <Badge
                              variant={
                                issue.status === 'resolved' ? 'default' :
                                issue.status === 'pending_verification' ? 'secondary' :
                                issue.status === 'fix_deployed' ? 'secondary' :
                                'outline'
                              }
                              className={
                                issue.status === 'pending_verification' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                                issue.status === 'fix_deployed' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                                ''
                              }
                            >
                              {issue.status === 'pending_verification' ? 'Pending Verification' :
                               issue.status === 'fix_deployed' ? 'Fix Deployed' :
                               issue.status === 'wont_fix' ? "Won't Fix" :
                               issue.status === 'false_positive' ? 'False Positive' :
                               issue.status}
                            </Badge>
                          )}
                        </div>
                        <p className="font-medium truncate">{issue.product_name}</p>
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {issue.description}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Quality: {issue.quality_score_avg != null ? Number(issue.quality_score_avg).toFixed(1) : 'N/A'}/10 | Confidence:{' '}
                          {issue.combined_confidence != null ? (Number(issue.combined_confidence) * 100).toFixed(0) : 'N/A'}%
                        </p>
                      </div>
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Issue Detail Dialog */}
      <Dialog open={!!selectedIssue} onOpenChange={(open) => !open && setSelectedIssue(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto overflow-x-hidden">
          {selectedIssue && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <div
                    className={`w-3 h-3 rounded-full ${
                      severityColors[selectedIssue.severity]
                    }`}
                  />
                  <DialogTitle>{selectedIssue.issue_type}</DialogTitle>
                  <Badge variant="outline">{selectedIssue.severity}</Badge>
                </div>
                <DialogDescription>{selectedIssue.product_name}</DialogDescription>
              </DialogHeader>

              <div className="space-y-4 w-full min-w-0">
                {/* Issue Info */}
                <Card className="overflow-hidden">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Issue Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm [word-break:break-word]">
                    <div>
                      <strong>Description:</strong> {selectedIssue.description}
                    </div>
                    <div>
                      <strong>Location:</strong> {selectedIssue.location}
                    </div>
                    <div>
                      <strong>Consensus:</strong> {consensusLabels[selectedIssue.consensus]}
                    </div>
                    <div>
                      <strong>Confidence:</strong>{' '}
                      {selectedIssue.combined_confidence != null ? (Number(selectedIssue.combined_confidence) * 100).toFixed(0) : 'N/A'}%
                    </div>
                    {selectedIssue.page_url && (
                      <div>
                        <strong>Page:</strong>{' '}
                        <a
                          href={selectedIssue.page_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:underline"
                        >
                          View Page <ExternalLink className="h-3 w-3 inline" />
                        </a>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Generated Content Preview */}
                {selectedIssue.aeo_content && (
                  <Card className="overflow-hidden">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Generated Content</CardTitle>
                    </CardHeader>
                    <CardContent className="overflow-hidden">
                      <ContentViewer
                        content={selectedIssue.aeo_content}
                        issueDescription={selectedIssue.description}
                        issueLocation={selectedIssue.location}
                      />
                    </CardContent>
                  </Card>
                )}

                {/* Evidence */}
                <Card>
                  <CardHeader className="pb-2 flex flex-row items-center justify-between">
                    <CardTitle className="text-sm">Evidence (Prompt & Context)</CardTitle>
                    {!selectedIssue.evidence_data && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => retrieveEvidence(selectedIssue.id)}
                        disabled={loadingEvidence}
                      >
                        {loadingEvidence ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <FileSearch className="h-4 w-4 mr-1" />
                            Retrieve from S3
                          </>
                        )}
                      </Button>
                    )}
                  </CardHeader>
                  <CardContent>
                    {selectedIssue.evidence_data ? (
                      <div className="space-y-4">
                        {/* Product info */}
                        {selectedIssue.evidence_data.product_name && (
                          <div className="p-2 bg-primary/10 rounded border border-primary/30">
                            <p className="text-xs text-muted-foreground">Product</p>
                            <p className="font-medium text-sm">{selectedIssue.evidence_data.product_name}</p>
                          </div>
                        )}

                        {/* Sections with prompts and responses */}
                        {selectedIssue.evidence_data.sections && (
                          <div className="space-y-3">
                            <p className="font-medium text-sm">Generation Prompts & Responses</p>
                            {Object.entries(selectedIssue.evidence_data.sections).map(([sectionName, sectionData]: [string, any]) => (
                              <details key={sectionName} className="border rounded p-2">
                                <summary className="cursor-pointer font-medium text-sm flex items-center gap-2">
                                  <span className="capitalize">{sectionName.replace(/([A-Z])/g, ' $1').trim()}</span>
                                  <Badge variant="outline" className="text-xs">{sectionData.model}</Badge>
                                </summary>
                                <div className="mt-2 space-y-2">
                                  <details className="ml-2">
                                    <summary className="cursor-pointer text-xs text-muted-foreground">Prompt</summary>
                                    <pre className="mt-1 text-xs bg-muted p-2 rounded overflow-auto max-h-40 whitespace-pre-wrap">
                                      {sectionData.prompt}
                                    </pre>
                                  </details>
                                  <details className="ml-2">
                                    <summary className="cursor-pointer text-xs text-muted-foreground">Response</summary>
                                    <pre className="mt-1 text-xs bg-muted p-2 rounded overflow-auto max-h-40 whitespace-pre-wrap">
                                      {sectionData.response}
                                    </pre>
                                  </details>
                                </div>
                              </details>
                            ))}
                          </div>
                        )}

                        {/* Raw JSON fallback */}
                        <details className="text-xs">
                          <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                            Show Raw JSON
                          </summary>
                          <pre className="mt-2 bg-muted p-3 rounded overflow-auto max-h-40">
                            {JSON.stringify(selectedIssue.evidence_data, null, 2)}
                          </pre>
                        </details>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        Click "Retrieve from S3" to load the prompt and context used for
                        this generation.
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* Resolution */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Resolution</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Status</Label>
                        <Select
                          key={selectedIssue.id}
                          value={selectedIssue.status || 'open'}
                          onValueChange={(v) =>
                            updateIssueStatus(selectedIssue.id, { status: v as any })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="open">Open</SelectItem>
                            <SelectItem value="investigating">Investigating</SelectItem>
                            <SelectItem value="fix_deployed">Fix Deployed</SelectItem>
                            <SelectItem value="pending_verification">Pending Verification</SelectItem>
                            <SelectItem value="resolved">Resolved</SelectItem>
                            <SelectItem value="wont_fix">Won't Fix</SelectItem>
                            <SelectItem value="false_positive">False Positive</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Root Cause</Label>
                        <Select
                          value={selectedIssue.root_cause_category || ''}
                          onValueChange={(v) =>
                            updateIssueStatus(selectedIssue.id, { root_cause_category: v })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select root cause" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="product_feed_data">Product Feed Data (Wrong in source)</SelectItem>
                            <SelectItem value="context">Context (Missing data)</SelectItem>
                            <SelectItem value="code">Code (Bug)</SelectItem>
                            <SelectItem value="prompt">Prompt (Unclear instruction)</SelectItem>
                            <SelectItem value="hallucination">Hallucination</SelectItem>
                            <SelectItem value="customer_feedback">Customer Feedback</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Won't Fix Guidance */}
                    {selectedIssue.status === 'wont_fix' && (
                      <Alert className="border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-950">
                        <Database className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                        <AlertDescription className="text-orange-700 dark:text-orange-300">
                          <strong>Feed Data Issue:</strong> This issue is caused by incorrect data in the product feed.
                          <ol className="list-decimal list-inside mt-2 text-sm space-y-1">
                            <li>Correct the data in your product feed source</li>
                            <li>Wait for the next feed import to update the product</li>
                            <li>Return here and click "Regenerate Content"</li>
                            <li>Click "Verify Fix" to confirm the issue is resolved</li>
                          </ol>
                        </AlertDescription>
                      </Alert>
                    )}

                    {/* Research Suggestion from Agent */}
                    {selectedIssue.evidence_data?.research_suggestion && (
                      <Alert className="border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-950">
                        <Lightbulb className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                        <AlertDescription className="text-purple-700 dark:text-purple-300">
                          <strong>Agent Research Suggestion:</strong>
                          <div className="mt-2 text-sm space-y-1">
                            <p><strong>Operation:</strong> {selectedIssue.evidence_data.research_suggestion.operation?.replace('_', ' ')}</p>
                            <p><strong>Concept:</strong> {selectedIssue.evidence_data.research_suggestion.concept_name}</p>
                            <p><strong>Category:</strong> {selectedIssue.evidence_data.research_suggestion.category_path}</p>
                            {selectedIssue.evidence_data.research_suggestion.context && (
                              <p><strong>Context:</strong> {selectedIssue.evidence_data.research_suggestion.context}</p>
                            )}
                            <p><strong>Status:</strong> <Badge variant={selectedIssue.evidence_data.research_suggestion.status === 'applied' ? 'default' : 'secondary'}>
                              {selectedIssue.evidence_data.research_suggestion.status}
                            </Badge></p>
                          </div>
                          {selectedIssue.evidence_data.research_suggestion.status !== 'applied' && (
                            <Button
                              className="mt-3"
                              variant="secondary"
                              size="sm"
                              onClick={() => applyResearch(selectedIssue.id)}
                              disabled={applyingResearch}
                            >
                              {applyingResearch ? (
                                <>
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  Applying Research...
                                </>
                              ) : (
                                <>
                                  <BookOpen className="h-4 w-4 mr-2" />
                                  Apply Research
                                </>
                              )}
                            </Button>
                          )}
                        </AlertDescription>
                      </Alert>
                    )}

                    {/* Research Result Feedback */}
                    {researchResult && (
                      <Alert className={researchResult.success
                        ? "border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950"
                        : "border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950"
                      }>
                        {researchResult.success ? (
                          <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                        ) : (
                          <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                        )}
                        <AlertDescription className={researchResult.success
                          ? "text-green-700 dark:text-green-300"
                          : "text-red-700 dark:text-red-300"
                        }>
                          {researchResult.message}
                        </AlertDescription>
                      </Alert>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-2 pt-2">
                      {(selectedIssue.status === 'open' || selectedIssue.status === 'fix_deployed' || selectedIssue.status === 'investigating') && (
                        <Button
                          onClick={() => regenerateIssue(selectedIssue.id)}
                          disabled={regenerating}
                        >
                          {regenerating ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Regenerating...
                            </>
                          ) : (
                            <>
                              <RefreshCw className="h-4 w-4 mr-2" />
                              Regenerate Content
                            </>
                          )}
                        </Button>
                      )}
                      {selectedIssue.status === 'pending_verification' && (
                        <Button
                          onClick={() => verifyIssue(selectedIssue.id)}
                          disabled={verifying}
                        >
                          {verifying ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Verifying...
                            </>
                          ) : (
                            <>
                              <CheckCircle2 className="h-4 w-4 mr-2" />
                              Verify Fix
                            </>
                          )}
                        </Button>
                      )}
                    </div>

                    {/* Status-specific guidance */}
                    {selectedIssue.status === 'fix_deployed' && (
                      <Alert>
                        <Target className="h-4 w-4" />
                        <AlertDescription>
                          Fix has been deployed. Click "Regenerate Content" to apply the fix to this product.
                        </AlertDescription>
                      </Alert>
                    )}
                    {regenerationJob && regenerating && (
                      <Alert className="border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950">
                        <Loader2 className="h-4 w-4 text-blue-600 dark:text-blue-400 animate-spin" />
                        <AlertDescription className="text-blue-700 dark:text-blue-300">
                          {regenerationJob.message}
                        </AlertDescription>
                      </Alert>
                    )}
                    {regenerationJob && !regenerating && regenerationJob.status === 'completed' && (
                      <Alert className="border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950">
                        <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                        <AlertDescription className="text-green-700 dark:text-green-300">
                          {regenerationJob.message}
                        </AlertDescription>
                      </Alert>
                    )}
                    {selectedIssue.status === 'pending_verification' && !regenerationJob && (
                      <Alert className="border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-950">
                        <Clock className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                        <AlertDescription className="text-yellow-700 dark:text-yellow-300">
                          Content has been regenerated. Click "Verify Fix" to re-evaluate with the LLM judge.
                        </AlertDescription>
                      </Alert>
                    )}

                    <div className="space-y-2">
                      <Label>Resolution Notes</Label>
                      <Textarea
                        value={selectedIssue.resolution_notes || ''}
                        onChange={(e) =>
                          setSelectedIssue({ ...selectedIssue, resolution_notes: e.target.value })
                        }
                        onBlur={() =>
                          updateIssueStatus(selectedIssue.id, {
                            resolution_notes: selectedIssue.resolution_notes,
                          })
                        }
                        placeholder="Describe how this was resolved..."
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
